/**
 * Chain Detection and Validation Utilities
 *
 * Provides utilities for:
 * - Auto-detecting blockchain from address format
 * - Validating addresses for each chain
 * - Formatting addresses for display
 * - Chain metadata and configuration
 */

// ============================================================================
// Chain Registry - Central Configuration
// ============================================================================
export const SUPPORTED_CHAINS = {
  // EVM-Compatible Chains (all share 0x... address format)
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum',
    symbol: 'ETH',
    icon: '⟠',
    color: '#627EEA',
    explorer: 'https://etherscan.io',
    // Address format: 0x + 40 hex characters
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 1, // Etherscan API V2 chain ID
    coinGeckoId: 'ethereum',
    defaultPriceUSD: 3300, // Fallback price if CoinGecko API unavailable
  },
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    symbol: 'POL',
    icon: '⬡',
    color: '#8247E5',
    explorer: 'https://polygonscan.com',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 137, // Etherscan API V2 chain ID
    coinGeckoId: 'matic-network', // Polygon (rebranded from MATIC to POL, CoinGecko ID unchanged)
    defaultPriceUSD: 0.85,
  },
  bsc: {
    id: 'bsc',
    name: 'BNB Chain',
    symbol: 'BNB',
    icon: '◆',
    color: '#F3BA2F',
    explorer: 'https://bscscan.com',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 56, // Etherscan API V2 chain ID
    coinGeckoId: 'binance-smart-chain',
    defaultPriceUSD: 930,
  },
  arbitrum: {
    id: 'arbitrum',
    name: 'Arbitrum',
    symbol: 'ARB',
    icon: '▲',
    color: '#28A0F0',
    explorer: 'https://arbiscan.io',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 42161, // Etherscan API V2 chain ID
    coinGeckoId: 'ethereum',
    defaultPriceUSD: 3300,
  },
  optimism: {
    id: 'optimism',
    name: 'Optimism',
    symbol: 'OP',
    icon: '🔴',
    color: '#FF0420',
    explorer: 'https://optimistic.etherscan.io',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 10, // Etherscan API V2 chain ID
    coinGeckoId: 'ethereum',
    defaultPriceUSD: 3300,
  },
  base: {
    id: 'base',
    name: 'Base',
    symbol: 'ETH',
    icon: '🔵',
    color: '#0052FF',
    explorer: 'https://basescan.org',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 8453, // Etherscan API V2 chain ID
    coinGeckoId: 'ethereum',
    defaultPriceUSD: 3300,
  },
  avalanche: {
    id: 'avalanche',
    name: 'Avalanche C-Chain',
    symbol: 'AVAX',
    icon: '🔺',
    color: '#E84142',
    explorer: 'https://snowtrace.io',
    addressLength: 42,
    addressPrefix: '0x',
    isEVM: true,
    chainId: 43114, // Etherscan API V2 chain ID
    coinGeckoId: 'avalanche',
    defaultPriceUSD: 15,
  },

  //
  // No API data for non-EVM chain now, disable them
  //
  // // Non-EVM Chains (unique address formats)
  // bitcoin: {
  //   id: 'bitcoin',
  //   name: 'Bitcoin',
  //   symbol: 'BTC',
  //   icon: '₿',
  //   color: '#F7931A',
  //   explorer: 'https://blockchair.com/bitcoin',
  //   // Address format: variable length, multiple formats
  //   addressLength: [26, 35, 42, 62], // Legacy, P2SH, SegWit ranges
  //   addressPrefix: ['1', '3', 'bc1'],
  //   isEVM: false,
  //   coinGeckoId: 'bitcoin',
  //   defaultPriceUSD: 100000,
  // },
  // bitcoincash: {
  //   id: 'bitcoincash',
  //   name: 'Bitcoin Cash',
  //   symbol: 'BCH',
  //   icon: '₿$', // Bitcoin Cash symbol variant
  //   color: '#8DC351',
  //   explorer: 'https://blockchair.com/bitcoin-cash',
  //   // Address format: CashAddr with or without prefix
  //   addressLength: [42, 54], // With and without 'bitcoincash:' prefix
  //   addressPrefix: ['bitcoincash:', 'q', 'p'],
  //   isEVM: false,
  //   coinGeckoId: 'bitcoin-cash',
  //   defaultPriceUSD: 500,
  // },
  // litecoin: {
  //   id: 'litecoin',
  //   name: 'Litecoin',
  //   symbol: 'LTC',
  //   icon: 'Ł',
  //   color: '#BEBEBE',
  //   explorer: 'https://blockchair.com/litecoin',
  //   // Address format: Similar to Bitcoin but starts with L or ltc1
  //   addressLength: [26, 35, 43, 63],
  //   addressPrefix: ['L', 'M', 'ltc1'],
  //   isEVM: false,
  //   coinGeckoId: 'litecoin',
  //   defaultPriceUSD: 105,
  // },
};

/**
 * Normalize chain
 * @param {string} chainName 
 * @returns 
 */
export const normalizeChain = (chainName) => {
  const chainLowered = chainName.toLocaleLowerCase();

  if (['eth', 'ethereum'].includes(chainLowered)) {
    return 'ethereum';
  }

  if (['pol', 'polygon',
    'polygon-pos' // CoinGecko name
  ].includes(chainLowered)) {
    return 'polygon';
  }

  if (['binance-smart-chain', 'bsc', 'bnb', 'bnb chain'].includes(chainLowered)) {
    return 'bsc';
  }

  if (['arb', 'arbitrum',
    'arbitrum-one' // CoinGecko name
  ].includes(chainLowered)) {
    return 'arbitrum';
  }

  if (['op', 'optimism',
    'optimistic-ethereum' // CoinGecko name
  ].includes(chainLowered)) {
    return 'optimism';
  }

  if (['base'].includes(chainLowered)) {
    return 'base';
  }

  if (['avax', 'avalanche',
    'avalanche c-chain', 'avalanche-c-chain'
  ].includes(chainLowered)) {
    return 'avalanche';
  }

  if (['btc', 'bitcoin'].includes(chainLowered)) {
    return 'bitcoin';
  }

  if (['bch', 'bitcoincash'].includes(chainLowered)) {
    return 'bitcoincash';
  }

  if (['ltc', 'litecoin'].includes(chainLowered)) {
    return 'litecoin';
  }

  throw new Error(`Unsupported chain '${chainName}'!`);
};

/**
 * Get chain configuration by ID
 */
export const getChain = (chainId) => {
  return SUPPORTED_CHAINS[chainId];
};

/**
 * Get all supported chains
 */
export const getAllChains = () => {
  return Object.values(SUPPORTED_CHAINS);
};

/**
 * Get enabled chains
 */
export const getEnabledChains = () => {
  return getAllChains();
};

/**
 * Get chain IDs as array
 */
export const getChainIds = () => {
  return Object.keys(SUPPORTED_CHAINS);
};

/**
 * Get all EVM-compatible chains
 */
export const getEVMChains = () => {
  return Object.values(SUPPORTED_CHAINS).filter(chain => chain.isEVM);
};

/**
 * Get enabled EVM chains only
 */
export const getEnabledEVMChains = () => {
  return getEnabledChains().filter(chain => chain.isEVM);
};

/**
 * Check if address is EVM format (0x + 40 hex chars)
 */
export const isEVMAddress = (address) => {
  return /^0x[a-fA-F0-9]{40}$/.test(address);
};

// ============================================================================
// Address Normalization
// ============================================================================

/**
 * Normalize address input
 * - Trim whitespace
 * - Remove line breaks
 * - Handle case sensitivity per chain
 */
export const normalizeAddress = (address, chain = null) => {
  if (!address) return '';

  // Remove all whitespace
  let normalized = address.trim().replace(/\s+/g, '');

  // Chain-specific normalization
  if (chain === 'ethereum') {
    // Ethereum addresses are case-insensitive but often checksummed
    // Keep original case for checksum validation
    return normalized;
  }

  if (chain === 'bitcoin' || chain === 'litecoin') {
    // Bitcoin/Litecoin addresses are case-sensitive
    return normalized;
  }

  if (chain === 'bitcoincash') {
    // Bitcoin Cash CashAddr is case-insensitive
    return normalized.toLowerCase();
  }

  return normalized;
};

// ============================================================================
// Chain Detection
// ============================================================================

/**
 * Auto-detect blockchain from address format
 * Returns chain ID or null if cannot detect
 */
export const detectChain = (address) => {
  if (!address) return null;

  const normalized = normalizeAddress(address);

  // Ethereum: 0x + 40 hex characters
  if (/^0x[a-fA-F0-9]{40}$/.test(normalized)) {
    return 'ethereum';
  }

  // Bitcoin: Legacy P2PKH (starts with 1)
  if (/^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(normalized)) {
    return 'bitcoin';
  }

  // Bitcoin: P2SH (starts with 3)
  if (/^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(normalized)) {
    return 'bitcoin';
  }

  // Bitcoin: SegWit (starts with bc1)
  if (/^bc1[a-z0-9]{39,59}$/.test(normalized)) {
    return 'bitcoin';
  }

  // Bitcoin Cash: CashAddr with prefix
  if (/^[qp][a-z0-9]{41}$/.test(normalized)) {
    return 'bitcoincash';
  }

  // Bitcoin Cash: CashAddr without prefix (starts with q or p)
  if (/^[qp][a-z0-9]{41}$/.test(normalized)) {
    return 'bitcoincash';
  }

  // Litecoin: Legacy (starts with L)
  if (/^L[a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(normalized)) {
    return 'litecoin';
  }

  // Litecoin: P2SH (starts with M)
  if (/^M[a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(normalized)) {
    return 'litecoin';
  }

  // Litecoin: SegWit (starts with ltc1)
  if (/^ltc1[a-z0-9]{39,59}$/.test(normalized)) {
    return 'litecoin';
  }

  // Could not detect
  return null;
};

// ============================================================================
// Address Validation
// ============================================================================

/**
 * Validate Ethereum address
 * Checks format and optionally checksum
 */
export const validateEthereumAddress = (address) => {
  if (!address) return false;

  // Basic format check
  if (!/^0x[a-fA-F0-9]{40}$/.test(address)) {
    return false;
  }

  // TODO: Add checksum validation (EIP-55) for extra security
  // For now, basic format is sufficient
  return true;
};

/**
 * Validate Bitcoin address
 * Checks format (full checksum validation requires library)
 */
export const validateBitcoinAddress = (address) => {
  if (!address) return false;

  // Legacy P2PKH (starts with 1)
  if (/^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return true;
  }

  // P2SH (starts with 3)
  if (/^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) {
    return true;
  }

  // SegWit (starts with bc1)
  if (/^bc1[a-z0-9]{39,59}$/.test(address)) {
    return true;
  }

  return false;
};

/**
 * Validate Bitcoin Cash address
 */
export const validateBitcoinCashAddress = (address) => {
  if (!address) return false;

  const normalized = normalizeAddress(address, 'bitcoincash');

  // CashAddr with prefix
  if (/^[qp][a-z0-9]{41}$/.test(normalized)) {
    return true;
  }

  // CashAddr without prefix
  if (/^[qp][a-z0-9]{41}$/.test(normalized)) {
    return true;
  }

  return false;
};

/**
 * Validate Litecoin address
 */
export const validateLitecoinAddress = (address) => {
  if (!address) return false;

  // Legacy (starts with L)
  if (/^L[a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(address)) {
    return true;
  }

  // P2SH (starts with M)
  if (/^M[a-km-zA-HJ-NP-Z1-9]{26,33}$/.test(address)) {
    return true;
  }

  // SegWit (starts with ltc1)
  if (/^ltc1[a-z0-9]{39,59}$/.test(address)) {
    return true;
  }

  return false;
};

/**
 * Validate address for specific chain
 */
export const validateAddress = (address, chain) => {
  if (!address || !chain) return false;

  // Check if chain is EVM-compatible (all use same address format)
  const chainConfig = SUPPORTED_CHAINS[chain];
  if (chainConfig && chainConfig.isEVM) {
    return validateEthereumAddress(address);
  }

  // Non-EVM chains with unique formats
  switch (chain) {
    case 'bitcoin':
      return validateBitcoinAddress(address);
    case 'bitcoincash':
      return validateBitcoinCashAddress(address);
    case 'litecoin':
      return validateLitecoinAddress(address);
    default:
      return false;
  }
};

/**
 * Validate and detect chain
 * Returns { valid: boolean, chain: string | null, error: string | null }
 */
export const validateAndDetectChain = (address) => {
  if (!address) {
    return { valid: false, chain: null, error: 'Address is required' };
  }

  const normalized = normalizeAddress(address);
  const chain = detectChain(normalized);

  if (!chain) {
    return {
      valid: false,
      chain: null,
      error: 'Unknown address format. Supported: Ethereum family, Bitcoin, Bitcoin Cash, Litecoin',
    };
  }

  // Check if the detected chain is enabled
  const enabledChains = getEnabledChains();
  const enabledChainIds = enabledChains.map(c => c.id);

  if (!enabledChainIds.includes(chain)) {
    const enabledNames = enabledChains.map(c => c.name).join(', ');
    return {
      valid: false,
      chain: chain,
      error: `${SUPPORTED_CHAINS[chain].name} is not currently supported. Currently supported: ${enabledNames}`,
    };
  }

  const valid = validateAddress(normalized, chain);

  if (!valid) {
    return {
      valid: false,
      chain: chain,
      error: `Invalid ${SUPPORTED_CHAINS[chain].name} address format`,
    };
  }

  return {
    valid: true,
    chain: chain,
    error: null,
  };
};

/**
 * Get full address (with prefix if needed)
 * Ensures Bitcoin Cash addresses always have the prefix for APIs
 */
export const getFullAddress = (address, chain) => {
  if (!address) return '';

  if (chain === 'bitcoincash' && !address.startsWith('bitcoincash:')) {
    return `bitcoincash:${address}`;
  }

  return address;
};

// ============================================================================
// Chain-Specific Helpers
// ============================================================================

/**
 * Get explorer URL for address
 */
export const getExplorerUrl = (address, chain) => {
  const chainConfig = SUPPORTED_CHAINS[chain];
  if (!chainConfig) return null;

  return `${chainConfig.explorer}/address/${address}`;
};

/**
 * Check if chain is supported
 */
export const isChainSupported = (chain) => {
  return chain in SUPPORTED_CHAINS;
};

/**
 * Get chain icon (emoji or text)
 */
export const getChainIcon = (chain) => {
  const chainConfig = SUPPORTED_CHAINS[chain];
  return chainConfig ? chainConfig.icon : '?';
};

/**
 * Get chain color for UI
 */
export const getChainColor = (chain) => {
  const chainConfig = SUPPORTED_CHAINS[chain];
  return chainConfig ? chainConfig.color : '#999999';
};

// ============================================================================
// Composite ID Functions (Multi-Chain Support)
// ============================================================================

/**
 * Create composite ID from chain + address
 * Format: "{chain}_{address}"
 * Example: "ethereum_0x1234..." or "bitcoin_1A1zP1..."
 *
 * @param {string} chain - Chain identifier (e.g., "ethereum", "bitcoin")
 * @param {string} address - Address string
 * @returns {string} Composite ID
 * @throws {Error} If chain is unsupported or inputs invalid
 */
export const createCompositeId = (chain, address) => {
  if (!chain || typeof chain !== 'string') {
    throw new Error('Chain identifier is required and must be a string');
  }

  if (!address || typeof address !== 'string') {
    throw new Error('Address is required and must be a string');
  }

  const normalizedChain = chain.toLowerCase().trim();

  if (!SUPPORTED_CHAINS[normalizedChain]) {
    throw new Error(`Unsupported chain: ${chain}. Supported chains: ${getChainIds().join(', ')}`);
  }

  const normalizedAddress = normalizeAddress(address, normalizedChain);

  return `${normalizedChain}_${normalizedAddress}`;
};

/**
 * Parse composite ID into chain + address components
 * @param {string} compositeId - Composite ID: "chain_address"
 * @returns {{chain: string, address: string}} Parsed components
 * @throws {Error} If composite ID is invalid
 */
export const parseCompositeId = (compositeId) => {
  if (!compositeId || typeof compositeId !== 'string') {
    throw new Error('Invalid composite ID: must be a non-empty string');
  }

  const underscoreIndex = compositeId.indexOf('_');

  if (underscoreIndex === -1) {
    throw new Error('Invalid composite ID format: missing underscore separator');
  }

  const chain = compositeId.substring(0, underscoreIndex);
  const address = compositeId.substring(underscoreIndex + 1);

  if (!chain || !address) {
    throw new Error('Invalid composite ID format: both chain and address must be present');
  }

  if (!SUPPORTED_CHAINS[chain]) {
    throw new Error(`Unsupported chain in composite ID: ${chain}`);
  }

  return { chain, address };
};

/**
 * Validate composite ID format
 * @param {string} compositeId - Composite ID to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidCompositeId = (compositeId) => {
  try {
    const { chain, address } = parseCompositeId(compositeId);
    return !!chain && !!address && isChainSupported(chain);
  } catch (error) {
    return false;
  }
};

/**
 * Migrate legacy address to composite ID
 * Assumes Ethereum if no chain specified (for backward compatibility)
 * @param {string} address - Plain address
 * @param {string} defaultChain - Default chain if not specified (default: "ethereum")
 * @returns {string} Composite ID
 */
export const migrateToCompositeId = (address, defaultChain = 'ethereum') => {
  if (!address) {
    throw new Error('Address is required');
  }

  // If already composite ID, return as-is
  if (isValidCompositeId(address)) {
    return address;
  }

  // Otherwise, create composite with default chain
  return createCompositeId(defaultChain, address);
};

/**
 * Safely extract plain address from composite ID or plain address
 * @param {string} addressOrComposite - Plain address or composite ID
 * @returns {string} Plain address
 */
export const extractAddress = (addressOrComposite) => {
  if (!addressOrComposite) {
    return '';
  }

  try {
    const { address } = parseCompositeId(addressOrComposite);
    return address;
  } catch (error) {
    // Not a composite ID, return as-is (plain address)
    return addressOrComposite;
  }
};

/**
 * Safely extract chain from composite ID
 * @param {string} addressOrComposite - Plain address or composite ID
 * @param {string} defaultChain - Default chain if not composite (default: "ethereum")
 * @returns {string} Chain identifier
 */
export const extractChain = (addressOrComposite, defaultChain = 'ethereum') => {
  if (!addressOrComposite) {
    return defaultChain;
  }

  try {
    const { chain } = parseCompositeId(addressOrComposite);
    return chain;
  } catch (error) {
    // Not a composite ID, return default
    return defaultChain;
  }
};

/**
 * Compare two addresses (handles both plain and composite)
 * @param {string} addr1 - First address (plain or composite)
 * @param {string} addr2 - Second address (plain or composite)
 * @returns {boolean} True if addresses are equal
 */
export const addressesEqual = (addr1, addr2) => {
  if (!addr1 || !addr2) {
    return false;
  }

  // Try to parse as composite IDs
  try {
    const parsed1 = parseCompositeId(addr1);
    const parsed2 = parseCompositeId(addr2);

    // Both are composite IDs - compare chain and address
    return parsed1.chain === parsed2.chain &&
           parsed1.address.toLowerCase() === parsed2.address.toLowerCase();
  } catch (error) {
    // At least one is not a composite ID - compare as plain strings
    return addr1.toLowerCase() === addr2.toLowerCase();
  }
};

// ============================================================================
// Native Token to CoinGecko Coin ID Mapping
// ============================================================================

/**
 * Maps native token symbols to their CoinGecko coin IDs for linking to coin pages
 * Used in Funded By table to link native tokens to their coin pages
 */
export const NATIVE_TOKEN_TO_COIN_ID = {
  // Ethereum and L2s
  'ETH': 'ethereum',
  'WETH': 'weth',

  // BNB Chain
  'BNB': 'binancecoin',
  'WBNB': 'wbnb',

  // Polygon (MATIC migrated to POL)
  'POL': 'matic-network',
  'MATIC': 'matic-network',

  // Avalanche
  'AVAX': 'avalanche-2',

  // Solana
  'SOL': 'solana',

  // Bitcoin and wrapped
  'BTC': 'bitcoin',
  'WBTC': 'wrapped-bitcoin',

  // Other L1 native tokens
  'LTC': 'litecoin',
  'BCH': 'bitcoin-cash',
  'DOGE': 'dogecoin',
  'XRP': 'ripple',
  'ADA': 'cardano',
  'DOT': 'polkadot',
  'TRX': 'tron',
  'NEAR': 'near',
  'ATOM': 'cosmos',
  'LINK': 'chainlink',
};

/**
 * Get CoinGecko coin ID for a native token symbol
 * @param {string} symbol - Token symbol (e.g., 'ETH', 'BNB')
 * @returns {string|null} CoinGecko coin ID or null if not found
 */
export const getNativeTokenCoinId = (symbol) => {
  if (!symbol) return null;
  return NATIVE_TOKEN_TO_COIN_ID[symbol.toUpperCase()] || null;
};

// ============================================================================
// Export all for convenience
// ============================================================================

const chainUtils = {
  // Registry
  SUPPORTED_CHAINS,
  getChain,
  getAllChains,
  getEnabledChains,
  getChainIds,
  isChainSupported,

  // Detection
  detectChain,
  validateAndDetectChain,

  // Validation
  validateAddress,
  validateEthereumAddress,
  validateBitcoinAddress,
  validateBitcoinCashAddress,
  validateLitecoinAddress,

  // Formatting
  normalizeAddress,
  normalizeChain,
  getFullAddress,

  // Helpers
  getExplorerUrl,
  getChainIcon,
  getChainColor,

  // Composite ID functions (NEW)
  createCompositeId,
  parseCompositeId,
  isValidCompositeId,
  migrateToCompositeId,
  extractAddress,
  extractChain,
  addressesEqual,

  // Native token mapping
  NATIVE_TOKEN_TO_COIN_ID,
  getNativeTokenCoinId,
};

export default chainUtils;
